import React from 'react';
import { Shield, Phone, Loader2, Check, Clock, CheckCircle2, Sparkles } from 'lucide-react';

const VerifyOtpStep = ({
    formData, phoneVerified, otpSent, otpSending, otpValues, otpVerifying,
    timer, resendCooldown, otpInputRefs,
    sendOtp, handleOtpChange, handleOtpKeyDown, handleOtpPaste,
    verifyOtp, submitDemo, isSubmitting
}) => (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
        {!phoneVerified ? (
            <>
                <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#041f96] to-[#0633d4] flex items-center justify-center shadow-lg shadow-blue-200">
                        <Shield className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Verify Your Phone</h3>
                    <p className="text-sm text-gray-500 mt-1">
                        {otpSent
                            ? `Enter the 6-digit code sent to +91 ${formData.phone}`
                            : `We'll send an OTP to +91 ${formData.phone}`
                        }
                    </p>
                </div>

                {!otpSent ? (
                    <button
                        onClick={sendOtp}
                        disabled={otpSending}
                        className="w-full py-3.5 bg-gradient-to-r from-[#041f96] to-[#0633d4] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-200 transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                        {otpSending ? (
                            <><Loader2 className="h-5 w-5 animate-spin" /> Sending OTP...</>
                        ) : (
                            <><Phone className="h-5 w-5" /> Send OTP</>
                        )}
                    </button>
                ) : (
                    <>
                        {/* OTP Input */}
                        <div className="flex justify-center gap-2">
                            {otpValues.map((val, idx) => (
                                <input
                                    key={idx}
                                    ref={el => otpInputRefs.current[idx] = el}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={val}
                                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                                    onPaste={idx === 0 ? handleOtpPaste : undefined}
                                    className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-[#041f96] focus:outline-none focus:ring-2 focus:ring-[#041f96]/20 transition-all"
                                />
                            ))}
                        </div>

                        {/* Timer */}
                        {timer > 0 && (
                            <p className="text-center text-sm text-gray-500 flex items-center justify-center gap-1">
                                <Clock className="h-4 w-4" />
                                OTP expires in {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}
                            </p>
                        )}

                        {/* Verify button */}
                        <button
                            onClick={verifyOtp}
                            disabled={otpVerifying || otpValues.join('').length !== 6}
                            className="w-full py-3.5 bg-gradient-to-r from-[#041f96] to-[#0633d4] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-200 transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                        >
                            {otpVerifying ? (
                                <><Loader2 className="h-5 w-5 animate-spin" /> Verifying...</>
                            ) : (
                                <><Check className="h-5 w-5" /> Verify OTP</>
                            )}
                        </button>

                        {/* Resend */}
                        <p className="text-center text-sm text-gray-500">
                            Didn't receive?{' '}
                            <button
                                onClick={sendOtp}
                                disabled={resendCooldown > 0 || otpSending}
                                className="text-[#041f96] font-semibold hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                            </button>
                        </p>
                    </>
                )}
            </>
        ) : (
            /* Phone verified — show submit */
            <div className="text-center space-y-6">
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-200">
                    <CheckCircle2 className="h-10 w-10 text-white" />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-gray-900">Phone Verified! ✅</h3>
                    <p className="text-sm text-gray-500 mt-1">Everything looks good. Submit to book your free demo.</p>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-2xl p-4 text-left space-y-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-gray-500">Category</span>
                        <span className="font-medium text-gray-900">{formData.category?.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">Subjects</span>
                        <span className="font-medium text-gray-900">{formData.subjects.join(', ')}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">Location</span>
                        <span className="font-medium text-gray-900">{formData.city}{formData.state ? `, ${formData.state}` : ''}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">Budget</span>
                        <span className="font-medium text-gray-900">₹{formData.budget_amount}/{formData.budget_type}</span>
                    </div>
                </div>

                <button
                    onClick={submitDemo}
                    disabled={isSubmitting}
                    className="w-full py-4 bg-gradient-to-r from-[#041f96] to-[#0633d4] text-white font-bold text-lg rounded-xl hover:shadow-xl hover:shadow-blue-200 transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                    {isSubmitting ? (
                        <><Loader2 className="h-5 w-5 animate-spin" /> Booking Demo...</>
                    ) : (
                        <><Sparkles className="h-5 w-5" /> Book Free Demo</>
                    )}
                </button>
            </div>
        )}
    </div>
);

export default VerifyOtpStep;
